import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";

export function usePageMeta(title, desc, path) {
  useEffect(() => {
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", `https://www.indiafast.in${path}`);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `https://www.indiafast.in${path}`);
  }, [title, desc, path]);
}

export function SectionHead({ kicker, title, sub, center, dark, testid }) {
  return (
    <div className={center ? "text-center" : ""} data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">{kicker}</p>
      <h2 className={`mt-3 font-heading text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl ${dark ? "text-white" : "text-navy"} ${center ? "mx-auto max-w-3xl" : "max-w-3xl"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-4 text-sm leading-relaxed sm:text-base ${dark ? "text-slate-300" : "text-slate-500"} ${center ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>{sub}</p>}
    </div>
  );
}

export function QuoteTrackButtons({ quoteLabel = "Get a Quote", quoteTo = "/rate-calculator", testid }) {
  return (
    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
      <Link to={quoteTo} data-testid={`${testid}-quote-cta`}
            className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
        {quoteLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
      <Link to="/track" data-testid={`${testid}-track-cta`}
            className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
        <Search className="h-4 w-4" /> Track Shipment
      </Link>
    </div>
  );
}

export function FinalCta({ img, title, sub, primaryLabel, primaryTo, primaryTestid, secondary, testid }) {
  return (
    <section className="relative overflow-hidden bg-navy py-20 sm:py-24" data-testid={testid}>
      {img && <img src={img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/40" />
      <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">{title}</h2>
        {sub && <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">{sub}</p>}
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Link to={primaryTo} data-testid={primaryTestid}
                className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
            {primaryLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          {secondary}
        </div>
      </div>
    </section>
  );
}

export function DestinationCard({ name, img, to = "/rate-calculator", testid }) {
  return (
    <Link to={to} data-testid={testid}
          className="group relative block overflow-hidden rounded-2xl shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
      <img src={img} alt={name} loading="lazy" className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4">
        <span className="font-heading text-base font-bold text-white">{name}</span>
        <span className="flex items-center gap-1 rounded-full bg-saffron px-3 py-1 text-[10px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100">
          Get Quote <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

export function StepsLine({ steps, testid, cols = "lg:grid-cols-4" }) {
  return (
    <div className={`relative mt-12 grid gap-8 sm:grid-cols-2 ${cols}`} data-testid={testid}>
      <span className="absolute left-0 right-0 top-6 hidden h-0.5 bg-gradient-to-r from-saffron via-slate-200 to-emerald-600 lg:block" />
      {steps.map((s, i) => (
        <div key={i} className="relative flex gap-4 sm:flex-col sm:gap-0" data-testid={`${testid}-step-${i}`}>
          <span className="relative z-10 grid h-12 w-12 shrink-0 place-items-center rounded-full bg-navy text-saffron ring-4 ring-white">
            {s.icon ? <s.icon className="h-5 w-5" /> : <span className="font-heading text-sm font-extrabold">{s.n}</span>}
          </span>
          <div className="sm:mt-4">
            <p className="font-heading text-base font-bold text-navy">{s.title}</p>
            {s.desc && <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.desc}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
